import axios from 'axios';
import logger from './logger.js';
import { buildLoginPathWithRedirect } from './authRedirect';
import {
  createMaintenanceBlockedError,
  notifyMaintenanceBlock,
  parseMaintenanceFromResponse,
  shouldBlockApiRequest,
} from './maintenanceBlock';

// Token keys for the client app.
const TOKEN_KEY = 'token';
const ORIGIN_KEY = 'tokenOrigin';
const GUEST_MODE_KEY = 'guestMode';

// Public auth endpoints where a 401 does NOT mean "session expired"
// (e.g. wrong password on login). We intentionally skip redirect here.
const AUTH_PUBLIC_ENDPOINTS = [
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/forgot-password',
  '/api/auth/reset-password',
];

// Front-end routes that are already "public" — if a 401 fires while the user
// is here (e.g. they're on the login page) there is no useful place to send
// them, so we just clear stored auth and stay put.
const PUBLIC_ROUTE_PREFIXES = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
];

const resolveRequestUrl = (input) => {
  if (typeof input === 'string') return input;
  if (input && typeof input.url === 'string') return input.url;
  return '';
};

const isPublicAuthUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  return AUTH_PUBLIC_ENDPOINTS.some((p) => url.includes(p));
};

const isOnPublicAuthRoute = () => {
  if (typeof window === 'undefined' || !window.location) return false;
  const path = window.location.pathname || '';
  return PUBLIC_ROUTE_PREFIXES.some(
    (p) => path === p || path.startsWith(`${p}/`)
  );
};

let isHandlingExpiry = false;
let installed = false;

// We intentionally do NOT gate on "token present in localStorage" here,
// because cross-tab logout, origin-mismatch cleanup, or any other external
// mutation can clear the token while the tab is still mid-session. Those are
// exactly the cases where we MUST redirect. Instead we only suppress when the
// 401 is from a public auth endpoint (bad login credentials, etc.) or the
// user has explicitly chosen guest mode.
const shouldIgnoreFor401 = (url) => {
  if (isPublicAuthUrl(url)) return true;
  if (localStorage.getItem(GUEST_MODE_KEY) === 'true') return true;
  return false;
};

const requestHadAuthorization = (error) => {
  const headers = error?.config?.headers || {};
  const headerAuth = headers.Authorization || headers.authorization;
  if (headerAuth) return true;
  return Boolean(axios.defaults.headers.common?.Authorization);
};

const clearStoredAuth = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ORIGIN_KEY);
    localStorage.removeItem(GUEST_MODE_KEY);
    delete axios.defaults.headers.common['Authorization'];
  } catch (_e) {
    // ignore storage access errors
  }
};

export const handleSessionExpired = (reason) => {
  if (isHandlingExpiry) return;
  isHandlingExpiry = true;

  logger.warn('Session expired. Redirecting to login.', reason || '');

  clearStoredAuth();

  // If the user is already on a public auth page, there is nowhere useful to
  // redirect them — just let the current page stand (now without stale auth).
  if (isOnPublicAuthRoute()) {
    return;
  }

  let loginPath = '/login';
  try {
    loginPath = buildLoginPathWithRedirect(
      typeof window !== 'undefined' ? window.location : null
    );
  } catch (_e) {
    loginPath = '/login';
  }

  if (typeof window !== 'undefined') {
    // Hard redirect so React state is fully reset and all in-flight
    // requests/components are torn down.
    window.location.replace(loginPath);
  }
};

const handleMaintenanceResponse = async (response) => {
  const maintenance = await parseMaintenanceFromResponse(response);
  if (maintenance) {
    notifyMaintenanceBlock(maintenance);
  }
};

export const installSessionExpiryHandlers = () => {
  if (installed) return;
  installed = true;

  axios.interceptors.request.use(
    (config) => {
      const url = config?.url || '';
      if (shouldBlockApiRequest(url)) {
        return Promise.reject(createMaintenanceBlockedError());
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Global axios response interceptor — covers every axios call in the app.
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      try {
        const status = error?.response?.status;
        const url =
          error?.config?.url || error?.response?.config?.url || '';
        const maintenance = error?.response?.data?.maintenance;
        if (status === 503 && maintenance) {
          notifyMaintenanceBlock(maintenance);
        } else if (
          status === 401
          && !shouldIgnoreFor401(url)
          && requestHadAuthorization(error)
        ) {
          handleSessionExpired(`axios 401 ${url}`);
        }
      } catch (_e) {
        // never let the interceptor itself throw
      }
      return Promise.reject(error);
    }
  );

  // Global fetch wrapper — covers raw fetch calls scattered across the app.
  if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
    const originalFetch = window.fetch.bind(window);
    window.fetch = async (...args) => {
      const url = resolveRequestUrl(args[0]);
      if (shouldBlockApiRequest(url)) {
        return Promise.reject(createMaintenanceBlockedError());
      }

      const response = await originalFetch(...args);
      try {
        if (response?.status === 503) {
          await handleMaintenanceResponse(response);
        } else if (response?.status === 401) {
          const input = args[1];
          const initHeaders = input && typeof input === 'object' ? input.headers : null;
          const headerAuth = initHeaders?.Authorization || initHeaders?.authorization;
          const hadAuth = Boolean(headerAuth || axios.defaults.headers.common?.Authorization);
          if (url.includes('/api/') && !shouldIgnoreFor401(url) && hadAuth) {
            handleSessionExpired(`fetch 401 ${url}`);
          }
        }
      } catch (_e) {
        // ignore — never break a real response on introspection errors
      }
      return response;
    };
  }

  // Cross-tab sync: if another tab clears the token (logout, expiry cleanup,
  // or any other reason), proactively redirect this tab to login without
  // waiting for the next failed API call to reveal the broken session.
  if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('storage', (event) => {
      try {
        // event.key === null means localStorage.clear() was called.
        const tokenRemoved =
          (event.key === TOKEN_KEY && event.newValue === null) ||
          event.key === null;
        if (!tokenRemoved) return;
        // If this tab already has no token in memory-bound axios default and
        // no token in storage, treat it as a logout on another tab.
        if (localStorage.getItem(TOKEN_KEY)) return;
        // Don't disturb users who are already on a public page.
        if (isOnPublicAuthRoute()) return;
        // Guest sessions should not be bounced to login.
        if (localStorage.getItem(GUEST_MODE_KEY) === 'true') return;
        handleSessionExpired('cross-tab token cleared');
      } catch (_e) {
        // ignore — never break on storage event handling
      }
    });
  }
};
